import { Component, computed, effect, inject, untracked } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { DIALOG_DATA, DialogRef, SbDialogModule, SbDialogService } from '@sb/ui/dialog';

import type { TemplateCategory } from '../../models/contract-template.model';
import { ContractTemplateUploadDialog } from '../../services/contract-template-upload-dialog';
import { DetectionProgressComponent } from '../detection-progress/detection-progress.component';
import {
  AiTemplateReviewModalComponent,
  type ReviewDialogData,
} from '../review-modal/ai-template-review-modal.component';
import { AiTemplateBuilderStore } from '../services/ai-template-builder.store';
import { UploadStepComponent } from '../upload-step/upload-step.component';

/**
 * The upload and scan views of the AI template builder, in a medium dialog. Once detection resolves it
 * opens the fullscreen review dialog and closes itself.
 */
@Component({
  selector: 'ai-template-builder-modal',
  templateUrl: './ai-template-builder-modal.component.html',
  imports: [DetectionProgressComponent, SbDialogModule, TranslatePipe, UploadStepComponent],
  providers: [AiTemplateBuilderStore],
})
export class AiTemplateBuilderModalComponent {
  #dialogRef = inject(DialogRef);
  #dialogService = inject(SbDialogService);
  #uploadDialog = inject(ContractTemplateUploadDialog);
  #data = inject<{ category?: TemplateCategory } | undefined>(DIALOG_DATA);

  store = inject(AiTemplateBuilderStore);

  progressState = computed(() => {
    const state = this.store.detectionState();

    return state === null || state === 'resolved' ? null : state;
  });

  constructor() {
    effect(() => {
      if (this.store.step() !== 0) {
        untracked(() => this.#openReview());
      }
    });
  }

  setUpManually(): void {
    this.#dialogRef.close();
    this.#uploadDialog.openManualUpload(this.#data);
  }

  #openReview(): void {
    const data: ReviewDialogData = {
      category: this.#data?.category,
      fileName: this.store.fileName(),
      resourceId: this.store.resourceId(),
      data: this.store.data(),
    };

    this.#dialogService.setSize('fullscreen').open(AiTemplateReviewModalComponent, { data });
    this.#dialogRef.close();
  }
}
